import backgroundTextureLavender from 'assets/images/vxl/component/productIntroScreen/background-texture-lavender.png';
import { Stack, useLocalSearchParams, useRouter } from 'expo-router';
import { navigateToDirectDepositSetup } from 'features/directDeposit/navigation/navigateToDirectDepositSetup';
import DirectDepositLoader from 'features/directDeposit/screens/DirectDepositLoader';
import { useEffect } from 'react';
import {
  ImageBackground,
  ImageSourcePropType,
  ScrollView,
  StyleSheet,
  View,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { parseOptionalGQLAnalyticsEvent } from 'services/analytics/helpers/parseGQLAnalyticsEvent';
import Icon from 'shared/components/vxl/icon/Icon/Icon';
import Illustration from 'shared/components/vxl/Illustration/Illustration';
import LoadingSpinner from 'shared/components/vxl/loading/LoadingSpinner/LoadingSpinner';
import { NavigationBar } from 'shared/components/vxl/navigation/NavigationBar/NavigationBar';
import { useViewDidFocusLogScreenAnalytics } from 'shared/hooks/analytics/useViewDidFocusAnalytics';
import { useDefaultErrorStrings } from 'shared/hooks/errorFormatters/useDefaultError/useDefaultErrorStrings';
import { useDirectDepositGateway } from 'shared/hooks/pinwheel/useDirectDepositGateway/useDirectDepositGateway';
import useDirectDepositManagementFlow from 'shared/hooks/pinwheel/useDirectDepositManagementFlow/useDirectDepositManagementFlow';
import usePinwheel from 'shared/hooks/pinwheel/usePinwheel/usePinwheel';
import ErrorScreen, {
  ErrorScreenType,
} from 'shared/patterns/vxl/errors/ErrorScreen/ErrorScreen';
import ScreenTransitionOverlay from 'shared/patterns/vxl/ScreenTransitionOverlay/ScreenTransitionOverlay';
import RendererWithHorizontalPadding from 'shared/renderer/RendererWithHorizontalPadding';
import { VXLComponentId, VXLNode } from 'shared/renderer/types';
import theme from 'shared/styles/theme';

import useDirectDepositBenefitsData from './useDirectDepositBenefitsData/useDirectDepositBenefitsData';

interface DirectDepositBenefitsScreenProps {
  isOnboardingFlow?: boolean;
  onPinwheelSuccess?: () => void | Promise<void>;
  flowStepId?: string;
}

const DirectDepositBenefitsScreen = ({
  isOnboardingFlow = false,
  onPinwheelSuccess,
  flowStepId,
}: DirectDepositBenefitsScreenProps) => {
  const router = useRouter();
  const { isPinwheelLoading } = usePinwheel();
  const { directDepositGatewayFlag } = useDirectDepositGateway();
  const { shouldRedirectToDDManagementPage } = useDirectDepositManagementFlow();

  const {
    fetching,
    error,
    directDepositBenefitsContent,
    refetch,
    isCompletingOnboardingFlow,
  } = useDirectDepositBenefitsData({
    isOnboardingFlow,
    onPinwheelSuccess,
  });

  const isTransitioningToNextOnboardingStep = Boolean(
    isCompletingOnboardingFlow,
  );

  const { abCohort, entry, fromSetupIntro } = useLocalSearchParams<{
    abCohort: string;
    sourceName?: string;
    entry?: string;
    fromSetupIntro?: string;
  }>();
  const { top, bottom } = useSafeAreaInsets();
  const { body, footer, displayAnalytics } = directDepositBenefitsContent ?? {};
  const { retryButtonTitle, cancelButtonTitle } = useDefaultErrorStrings();
  const analytics = parseOptionalGQLAnalyticsEvent(displayAnalytics);
  useViewDidFocusLogScreenAnalytics(analytics);
  useEffect(() => {
    if (
      !isOnboardingFlow &&
      shouldRedirectToDDManagementPage &&
      directDepositGatewayFlag
    ) {
      router.replace('/move-money/direct-deposit/direct-deposit-management');
    }
    if (
      !isOnboardingFlow &&
      shouldRedirectToDDManagementPage &&
      !directDepositGatewayFlag
    ) {
      if (router.canDismiss()) {
        router.dismiss();
      }
      router.replace('/move-money/direct-deposit/direct-deposit-management');
    }
  }, [
    isOnboardingFlow,
    shouldRedirectToDDManagementPage,
    directDepositGatewayFlag,
    router,
  ]);

  if (
    !isOnboardingFlow &&
    shouldRedirectToDDManagementPage &&
    directDepositGatewayFlag
  ) {
    return null;
  }

  const handleSetupDirectDeposit = () => {
    if (fromSetupIntro) {
      router.back();
      return;
    }

    if (!entry) {
      navigateToDirectDepositSetup({
        entry: isOnboardingFlow
          ? 'onboarding-direct-deposit-benefits'
          : 'direct-deposit-benefits',
        flowId: isOnboardingFlow ? flowStepId : undefined,
        sourceName: 'deposit_tracker',
        abCohort: abCohort,
        isCoinLoader: false,
        standAloneEntry: true,
      });
      return;
    }

    router.back();
  };

  const withSetupButtonHandler = (nodes: VXLNode[]): VXLNode[] => {
    return nodes.map((element) => {
      if ('name' in element && element.name === VXLComponentId.BUTTON) {
        return {
          ...element,
          props: {
            ...element.props,
            onPress: handleSetupDirectDeposit,
          },
        };
      }
      return element;
    });
  };

  const resolvedFooter =
    footer && directDepositGatewayFlag
      ? withSetupButtonHandler(footer)
      : footer;

  return (
    <View style={styles.container}>
      <Stack.Screen
        options={{
          title: '',
          headerShown: true,
          headerBackVisible: true,
          headerTransparent: true,
          header: () => (
            <NavigationBar
              title=""
              primaryIcon={<Icon iconKey="BACK" iconSize="MD" />}
              primaryOnPress={() => router.back()}
              transparent={true}
            />
          ),
        }}
      />
      <ScrollView stickyHeaderIndices={[0]}>
        <ImageBackground
          source={backgroundTextureLavender as ImageSourcePropType}
          style={styles.headerContentBackground}
        >
          <View style={styles.headerContentContainer}>
            <View style={styles.headerVisualContainer}>
              <Illustration illustration="CASH_A" />
            </View>
          </View>
        </ImageBackground>
        <View
          style={[
            styles.bodyContainer,
            styles.stackInFrontOfHeader,
            styles.textContainer,
          ]}
        >
          {body?.map((element) => {
            return (
              <RendererWithHorizontalPadding
                key={element.id}
                toRender={element}
                paddingHorizontal="m"
              />
            );
          })}
        </View>
      </ScrollView>
      {resolvedFooter ? (
        <View
          style={[
            styles.footerContainer,
            { paddingBottom: bottom + theme.spacing.m },
          ]}
        >
          {resolvedFooter.map((element) => (
            <RendererWithHorizontalPadding
              key={element.id}
              toRender={element}
              paddingHorizontal="m"
            />
          ))}
        </View>
      ) : null}
      <ScreenTransitionOverlay
        isLoading={
          fetching || isPinwheelLoading || isTransitioningToNextOnboardingStep
        }
        LoadingComponent={
          <DirectDepositLoader
            isLoadingConditions={fetching}
            isPinwheelLoading={isPinwheelLoading}
            defaultLoadingComponent={
              <View style={{ paddingTop: top + theme.spacing.m }}>
                <LoadingSpinner />
              </View>
            }
          />
        }
        ErrorComponent={
          error ? (
            <ErrorScreen
              screenType={ErrorScreenType.Error}
              messageHeadline={error.title}
              messageBody={error.message}
              primaryButton={{
                title: retryButtonTitle,
                onPress: () => refetch({ requestPolicy: 'network-only' }),
              }}
              secondaryButton={{
                title: cancelButtonTitle,
                onPress: () => {
                  if (router.canGoBack()) {
                    router.back();
                  }
                },
              }}
            />
          ) : null
        }
      />
    </View>
  );
};
const styles = StyleSheet.create({
  bodyContainer: { paddingVertical: theme.spacing.m },
  container: {
    backgroundColor: theme.color.background.primary,
    flex: 1,
    flexDirection: 'column',
    flexGrow: 1,
  },
  footerContainer: {
    borderColor: theme.color.border.primary,
    borderTopWidth: 1,
    paddingVertical: theme.spacing.m,
  },
  headerContentBackground: { height: 375, overflow: 'hidden' },
  headerContentContainer: {
    alignItems: 'center',
    flexDirection: 'column',
    height: '100%',
    justifyContent: 'flex-end',
    padding: theme.spacing.m,
    width: '100%',
  },
  headerVisualContainer: { height: 200, width: 200 },
  stackInFrontOfHeader: {
    zIndex: 11, // stickyHeaderIndices applies a zIndex of 10, so using 11 to stack in front of it
  },
  textContainer: {
    backgroundColor: theme.color.background.primary,
    textAlign: 'center',
  },
});
export default DirectDepositBenefitsScreen;
